import React, { useState } from "react";
import { Paper } from "@mui/material";
import SelectionClearWarning from "./SelectionClearWarning";
import TableFiltersAndColumnSettings from "./TableFiltersAndColumnSettings";
import TableHeaderAndActions from "./TableHeaderAndActions";
import GenericDraggableTable from "./GenericDraggableTable";
import DownloadCSV from "../../../Helper/CSVOperations/DownloadCSV";
import NameTheCSV from "../../../Helper/CSVOperations/NameTheCSV";
import { MAX_COLUMN_SELECTION_COUNT } from "../../../Helper/DigitalTwin/DataFile";
import { genericSearchSuggestions } from "../../../Api/DigitalTwinListApi";


export default function GenericDraggableTableView({
    title,
    count,
    primaryButtonLabel,
    showPrimaryButton,
    onPrimaryButtonClick,
    applySearch,
    disableSearch = false,
    searchSuggestionsHandler = genericSearchSuggestions,
    searchableFields = undefined,
    getTotalRowSelection = () => { },
    getTotalRowSelectionCount = () => { },
    getCsvColumnSettings = () => false,
    getCSVData,
    getCSVFileName,
    onSendEmail,
    showEmailComposer = true,
    availableEmailDomains,
    emailInfo = {},
    filters={},
    selectedFilters,
    applyFilters,
    initialColumns,
    selectedColumns,
    disableDownloads = false,
    disableFiltersSettings = false,
    disableColumnSettings = false,
    applySelectedColumns,
    filteredCount,
    tableColumns,
    tableData,
    onColumnOrderChange,
    columnOrder,
    isLoading,
    pageNumber,
    rowsPerPage = 20,
    total,
    applySort,
    onPageChange,
    emptyMessage = "No matches found for filters applied",
    rowSelection,
    expandable,
    setSelectedRowKeys,
    onClearSelection,
    disablePagination =  false,
    containerStyles = {},
    customRightActions = false,
    customRightActionsView = null,
    maxColumnsAllowed = MAX_COLUMN_SELECTION_COUNT,
    defaultSelectedColumns = [],
    initialSorting = {},
    disableTableHeader = false,
    alwaysEnableActions = false,
}) {

    const [isWarningOpen, setIsWarningOpen] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);
    const [isDownloading, setIsDownloading] = useState(false);

    const onDownloadCSV = async () => {
        if (isDownloading) return; // Prevent multiple simultaneous downloads

        setIsDownloading(true);
        try {
            const csvData = await getCSVData()
            let csvColumnSettings =  getCsvColumnSettings()
            if (!csvColumnSettings) {
                csvColumnSettings =  {columnOrder}
            }
            const orderKeys = csvColumnSettings.columnOrder
            const reorderedCSVData = csvData.map((item) =>
                Object.fromEntries(orderKeys.map((key) => [key, item[key] ?? ""]))
            );

            const csvName = getCSVFileName ? getCSVFileName() : NameTheCSV("digitalTwinCSV", "Download CSV")
            DownloadCSV( csvName, reorderedCSVData)
        } catch (error) {
            console.error("Error downloading CSV:", error);
        } finally {
            setIsDownloading(false);
        }
    }

    const getFixedColumnCount = () => {
        return tableColumns?.filter((columnConfig) => columnConfig?.fixed == 'left')?.length || 0
    }

    const confirmSelectionClear = (action) => {
        if (getTotalRowSelection()?.length > 0) {
            setPendingAction(() => action);
            setIsWarningOpen(true);
        } else {
            action();
        }
    };

    const handleConfirm = () => {
        setSelectedRowKeys && setSelectedRowKeys({});
        if (pendingAction) {
            pendingAction();
            setPendingAction(null);
        }
        setIsWarningOpen(false);
        onClearSelection && onClearSelection();
    };

    const handleCancel = () => {
        setIsWarningOpen(false);
        setPendingAction(null);
    };

    const onSearchApply = (selectedDevices, selectedSearchColumn) => {
        confirmSelectionClear(() => {
            applySearch(selectedDevices, selectedSearchColumn);
        });
    }

    const onFiltersApply = (args) => {
        confirmSelectionClear(() => {
            applyFilters(args);
        });
    }

    const onSortApply = (key, order) => {
        confirmSelectionClear(() => {
            applySort(key, order)
        });
    }

    return (
        <Paper elevation={0} 
            sx={{ 
                px: 3,
                py: 2, 
                height: "100%",
                borderRadius: "12px", 
                border: "1px solid #ececec" ,
                ...containerStyles
            }}>
            {!disableTableHeader && (
                <TableHeaderAndActions
                    title={title}
                    count={count}
                    primaryButtonLabel={primaryButtonLabel}
                    onSearchApply={onSearchApply}
                    searchableFields={searchableFields}
                    disableSearch={disableSearch}
                    searchSuggestionsHandler={searchSuggestionsHandler}
                    selectedRows={getTotalRowSelection()}
                    totalRowSelectionCount={getTotalRowSelectionCount ? getTotalRowSelectionCount() : getTotalRowSelection().length}
                    onDownloadCSV={onDownloadCSV}
                    getCSVData={getCSVData}
                    onSendEmail={onSendEmail}
                    showEmailComposer={showEmailComposer}
                    emailInfo={emailInfo}
                    availableEmailDomains={availableEmailDomains || []}
                    showPrimaryButton={showPrimaryButton}
                    disableDownloads = {disableDownloads}
                    onPrimaryButtonClick={() => onPrimaryButtonClick()}
                    customRightActions = {customRightActions}
                    customRightActionsView = {customRightActionsView}
                    isDownloading={isDownloading}
                    alwaysEnableActions={alwaysEnableActions}
                />
            )}

            <TableFiltersAndColumnSettings
                selectedRows={getTotalRowSelection()}
                filters={filters}
                selectedFilters={selectedFilters}
                setSelectedFilters={onFiltersApply}
                initialColumns={initialColumns}
                selectedColumns={selectedColumns}
                setSelectedColumns={applySelectedColumns}
                filteredCount={filteredCount}
                disableColumnSettings={disableColumnSettings}
                disableFiltersSettings={disableFiltersSettings ||  Object.keys(filters).length === 0}
                maxColumnsAllowed={maxColumnsAllowed}
                defaultSelectedColumns={defaultSelectedColumns}
            />

            <GenericDraggableTable
                initialColumns={tableColumns}
                data={tableData}
                onColumnChange={onColumnOrderChange}
                isLoading={isLoading}
                pageNumber={pageNumber}
                rowsPerPage={rowsPerPage}
                total={total}
                onSortChange={(key, order) => onSortApply(key, order)}
                onPageChange={(pageNo) => onPageChange(pageNo)}
                onPageSizeChange={() => { }}
                emptyMessage={emptyMessage}
                numberOfFixedColumns={getFixedColumnCount()}
                rowSelection={rowSelection}
                expandable = {expandable}
                disablePagination = {disablePagination}
                initialSorting={initialSorting}
            />

            <SelectionClearWarning
                open={isWarningOpen}
                onProceed={handleConfirm}
                onCancel={handleCancel}
            />

        </Paper>
    )
}
